import { type IReactionDisposer, makeAutoObservable, runInAction, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew'
import { showToast } from 'shared/ui'
import { RequestLoadData } from 'shared/api/lib'
import type {
  IParamsNumbers,
  IResponseIntegrationNumber,
  IResponseNumber,
} from 'entities/Phone/api/types'
import { PhoneApi } from 'entities/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AssignInboxStore } from 'entities/Phone/ui/actions/assignInbox/ui'
import { WebsocketEvent } from 'entities/WebSocket/store/type'
import type { IResponseIntegrationRefreshed } from 'entities/Integrations/api/types'
import { type IAssignType } from 'entities/Phone/ui/AssignNumberCommon/store/AssignNumberCommonStore'
import { AircallNumberRow } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/model/AircallNumberRow/AircallNumberRow'

export class AircallNumbersListStore {
  private readonly _tableStore: TableStore<AircallNumberRow>
  private _itemsMap: Map<number, AircallNumberRow> = new Map()
  private _scrollToTOpTrigger = ''
  private _firstLoading = true
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _refreshCountdown = 0
  private _refreshTimer: NodeJS.Timeout | null = null
  private _rejectAssignedNumbers: IResponseNumber[] = []
  private _rejectMessage: string[] | null = null
  private _search = ''
  private _waitingForUserDataUnsubscribe: (() => void) | null = null
  private _waitingForBulkCreationFinishedUnsubscribe: (() => void) | null = null

  constructor(private _entities: IEntitiesStore) {
    this._tableStore = new TableStore<AircallNumberRow>({
      key: 'aircall-numbers-list',
      element: 'number',
      sortOrder: 'asc',
      sortBy: undefined,
      limit: 500,
      page: 1,
    })

    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsGetItems()
    return this.dispose
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get paramsWithoutPage(): Omit<IParamsNumbers, 'page'> {
    return {
      limit: this._tableStore.limit,
      sortBy: this._tableStore.sortBy || undefined,
      sortOrder: this._tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IParamsNumbers {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get filteredItems() {
    if (!this._search.trim()) {
      return this.items
    }

    return this.items.filter((item) => {
      const cleanNumber = item.formatted_number.replace(/[^\d+]/g, '').toLowerCase()

      return cleanNumber.includes(this._search.toLowerCase())
    })
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get tableStore() {
    return this._tableStore
  }

  get firstLoading() {
    return this._firstLoading
  }

  get loading() {
    return this._tableStore.loading
  }

  get activeItems() {
    return this.filteredItems.filter((item) => item.isActive)
  }

  get assignedNumbersLength() {
    return this.items.filter((item) => item.isAssigned).length
  }

  get unassignedNumbersLength() {
    return this.items.filter((item) => !item.isAssigned).length
  }

  get assignedItems() {
    return this.filteredItems.filter((item) => item.isAssigned)
  }

  get unassignedItems() {
    return this.filteredItems.filter((item) => !item.isAssigned)
  }

  get totalItems() {
    return this.filteredItems.length
  }

  get refreshCountdown() {
    return this._refreshCountdown
  }

  get isRefreshDisabled() {
    return this.loading || this._refreshCountdown > 0
  }

  get refreshButtonLabel() {
    return this._refreshCountdown > 0
      ? `Refresh numbers in ${this._refreshCountdown}s`
      : 'Refresh numbers'
  }

  get importButtonLabel() {
    return this._refreshCountdown > 0
      ? `Import numbers in ${this._refreshCountdown}s`
      : 'Import numbers'
  }

  get rejectAssignedNumbers() {
    return this._rejectAssignedNumbers
  }

  get search() {
    return this._search
  }

  get isNumbersGracePeriod() {
    return this._entities.organizationStore.isNumbersGracePeriod
  }

  setSearch = (search: string) => {
    this._search = search.replace(/[^\d+]/g, '')
  }

  startRefreshCountdown = () => {
    this._refreshCountdown = 60
    this._startCountdownTimer()
  }

  private _startCountdownTimer = () => {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer)
    }

    if (this._refreshCountdown > 0) {
      this._refreshTimer = setTimeout(() => {
        runInAction(() => {
          this._refreshCountdown--
          if (this._refreshCountdown > 0) {
            this._startCountdownTimer()
          }
        })
      }, 1000)
    }
  }

  refreshNumbers = async () => {
    this.startRefreshCountdown()

    try {
      await PhoneApi.refreshAircallNumbers()

      await this.waitForUserDataUpdate()
    } catch (error) {
      console.error(error)
    }
  }

  private waitForUserDataUpdate = (): Promise<void> => {
    return new Promise((resolve) => {
      const userId = this._entities.usersStore.user?.id
      if (!userId) {
        resolve()
        return
      }

      this._waitingForUserDataUnsubscribe = this._entities.websocket.on(
        WebsocketEvent.IntegrationNumbersRefreshed,
        async (data: IResponseIntegrationRefreshed) => {
          if (data.integration_user.integration.key === 'aircall' && data.user === userId) {
            await this.loadData()

            showToast({
              title: 'Numbers refreshed',
              type: 'success',
            })

            this._waitingForUserDataUnsubscribe?.()
            this._waitingForUserDataUnsubscribe = null
            resolve()
          }
        }
      )
    })
  }

  private waitForBulkCreationFinished = (): Promise<void> => {
    return new Promise((resolve) => {
      const userId = this._entities.usersStore.user?.id
      if (!userId) {
        resolve()
        return
      }
      this._waitingForBulkCreationFinishedUnsubscribe?.()
      this._waitingForBulkCreationFinishedUnsubscribe = null

      this._waitingForBulkCreationFinishedUnsubscribe = this._entities.websocket.on(
        WebsocketEvent.InboxBulkCreationFinished,
        async () => {
          await this.loadData()

          this._waitingForBulkCreationFinishedUnsubscribe?.()
          this._waitingForBulkCreationFinishedUnsubscribe = null
          resolve()
        }
      )
    })
  }

  private _setItem = (data: IResponseIntegrationNumber) => {
    runInAction(() => {
      this._itemsMap.set(data.id, new AircallNumberRow(data))
    })
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      const { data } = await PhoneApi.getAircallNumbers(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          if (item.id) {
            this._setItem(item)
          }
        })
        this._tableStore.setTotal(data.meta.total)
        this._scrollToTOpTrigger = nanoid()
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
        this._firstLoading = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  assignNumbers = (selectedIds: number[]) => {
    const selectedNumbers = selectedIds
      .map((id) => {
        const row = this.getItem(id)
        if (!row) return null

        const numberData: IResponseNumber = {
          id: row.id,
          is_aircall: true,
          aircall_number_id: row.id.toString(),
          formatted_number: row.formatted_number,
          number: row.formatted_number,
          national_number: row.formatted_number,
          type: 'Aircall',
        } as IResponseNumber

        return numberData
      })
      .filter(Boolean) as IResponseNumber[]

    if (selectedNumbers.length === 0) return

    const handleAssignmentComplete = async ({
      processedAssignedNumbers,
      rejectAssignedNumbers,
      successAssignedNumbers,
      assignType,
      rejectMessage,
    }: {
      rejectAssignedNumbers?: IResponseNumber[]
      processedAssignedNumbers?: IResponseNumber[]
      successAssignedNumbers?: IResponseNumber[]
      assignType?: IAssignType
      rejectMessage?: string[] | null
    }) => {
      runInAction(() => {
        this._rejectAssignedNumbers = rejectAssignedNumbers || []
        this._rejectMessage = rejectMessage || null
      })

      if (assignType === 'new_per_number') {
        await this.waitForBulkCreationFinished()

        this.tableStore.resetSelected()
        return
      }

      if (processedAssignedNumbers?.length || successAssignedNumbers?.length) {
        this.loadData()
        this.tableStore.resetSelected()
      }
    }

    new AssignInboxStore(selectedNumbers, handleAssignmentComplete, this._entities, true)
  }

  getItem = (id: number) => {
    return this._itemsMap.get(id)
  }

  hasItem = (id: number) => {
    return this._itemsMap.has(id)
  }

  getNumbersByUser = (userId: number) => {
    return this.items.filter((item) => item.originalData.users.some((user) => user.id === userId))
  }

  clearRejectAssignedNumbers = () => {
    runInAction(() => {
      this._rejectAssignedNumbers = []
    })
  }

  clearRejectAssignedMessage = (index?: number) => {
    runInAction(() => {
      if (
        index !== undefined &&
        this._rejectMessage &&
        Array.isArray(this._rejectMessage) &&
        !!this._rejectMessage.length
      ) {
        this._rejectMessage = this._rejectMessage.filter((_, i) => i !== index)
      } else {
        this._rejectMessage = null
      }
    })
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()

    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer)
    }

    if (this._waitingForUserDataUnsubscribe) {
      this._waitingForUserDataUnsubscribe()
      this._waitingForUserDataUnsubscribe = null
    }

    if (this._waitingForBulkCreationFinishedUnsubscribe) {
      this._waitingForBulkCreationFinishedUnsubscribe()
      this._waitingForBulkCreationFinishedUnsubscribe = null
    }
  }

  get rejectMessage() {
    return this._rejectMessage
  }
}
